import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { SearchField } from '@/design-system/search-field';
import { Spinner } from '@/design-system/spinner';
import { toast } from '@/design-system/toaster';
import {
  Invoice,
  InvoiceExportError,
  RiderBookingGroup,
  RiderPerformanceRow,
  invoicesAPI,
} from '@/services/api/invoices';

import { AgingBadge, InvoiceStatusPill } from './InvoiceBadges';

interface RiderDetailPanelProps {
  /** Rider row to drill into. Null = drawer closed. */
  rider: RiderPerformanceRow | null;
  hubId: string;
  hubName: string;
  onClose: () => void;
  onOpenBoard: (driverId: string, driverName: string) => void;
}

/** Stable key for a booking group; the null-booking bucket gets its own key. */
const NO_BOOKING_KEY = '__NO_BOOKING__';
const groupKey = (g: RiderBookingGroup): string =>
  g.bookingId ?? NO_BOOKING_KEY;

const RiderDetailPanel: React.FC<RiderDetailPanelProps> = ({
  rider,
  hubId,
  hubName,
  onClose,
  onOpenBoard,
}) => {
  const open = rider !== null;
  const driverId = rider?.driverId ?? null;

  const PAGE_SIZE = 20;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [groups, setGroups] = useState<RiderBookingGroup[]>([]);
  const [total, setTotal] = useState(0); // total booking groups
  const [page, setPage] = useState(1);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false); // initial / reset load
  const [loadingMore, setLoadingMore] = useState(false); // lazy next page
  const [exporting, setExporting] = useState(false);

  const bodyRef = useRef<HTMLDivElement | null>(null); // drawer scroll container
  const sentinelRef = useRef<HTMLDivElement | null>(null); // infinite-scroll trigger
  const loadingMoreRef = useRef(false); // sync guard vs. observer double-fire

  const hasMore = groups.length < total;

  const toggleGroup = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Reset the search whenever the drawer opens for a different rider.
  useEffect(() => {
    setSearch('');
    setDebouncedSearch('');
  }, [driverId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 500);
    return () => clearTimeout(t);
  }, [search]);

  // First page — also re-runs (replacing the list) on rider / search change.
  useEffect(() => {
    if (!open || !driverId) return;
    let cancelled = false;
    setLoading(true);
    setPage(1);
    setCollapsed(new Set());
    invoicesAPI
      .riderBookings({
        driverId,
        hubId,
        search: debouncedSearch || undefined,
        page: 1,
        pageSize: PAGE_SIZE,
      })
      .then((data) => {
        if (!cancelled) {
          setGroups(data.data);
          setTotal(data.total);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setGroups([]);
          setTotal(0);
          toast.error('Could not load rider bookings', {
            description: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, driverId, hubId, debouncedSearch]);

  // Append the next page. A ref guard stops a burst of intersection events
  // from firing overlapping requests.
  const loadMore = useCallback(async () => {
    if (!driverId || loadingMoreRef.current) return;
    if (groups.length >= total) return;
    const nextPage = page + 1;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const data = await invoicesAPI.riderBookings({
        driverId,
        hubId,
        search: debouncedSearch || undefined,
        page: nextPage,
        pageSize: PAGE_SIZE,
      });
      setGroups((prev) => [...prev, ...data.data]);
      setTotal(data.total);
      setPage(nextPage);
    } catch (err) {
      toast.error('Could not load more bookings', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [driverId, hubId, debouncedSearch, page, groups.length, total]);

  // Lazy-load: observe a sentinel at the bottom of the drawer's scroll body.
  // rootMargin pre-fetches just before the sentinel reaches the viewport.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!open || !driverId || !sentinel || !hasMore || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: bodyRef.current ?? null, rootMargin: '120px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, driverId, hasMore, loading, loadMore]);

  const handleExport = async () => {
    if (!driverId) return;
    setExporting(true);
    try {
      await invoicesAPI.export({ driverId, hubId, format: 'csv' });
      toast.success('CSV export started');
    } catch (err) {
      const msg =
        err instanceof InvoiceExportError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Unknown error';
      toast.error('Export failed', { description: msg });
    } finally {
      setExporting(false);
    }
  };

  const title = useMemo(() => {
    if (!rider) return '';
    return (
      <div className="flex items-center gap-2">
        <span>
          {rider.driverName} - Driver ID: {rider.driverId}
        </span>
      </div>
    );
  }, [rider]);

  const footer = rider ? (
    <div className="flex gap-2">
      <Button
        tone="neutral"
        style="stroke"
        className="flex-1 justify-center"
        disabled={!driverId}
        onClick={() => driverId && onOpenBoard(driverId, rider.driverName)}
      >
        View in status board
      </Button>
      <Button
        className="flex-1 justify-center"
        disabled={!driverId || exporting}
        onClick={handleExport}
      >
        <i className="ri-download-2-line mr-1.5 text-[14px]" />
        {exporting ? 'Exporting…' : 'Export CSV rider'}
      </Button>
    </div>
  ) : undefined;

  return (
    <FormModal
      open={open}
      onClose={onClose}
      position="right"
      size="md"
      padded={false}
      contentRef={bodyRef}
      title={title}
      subtitle={
        rider ? (
          <span className="text-[11px]">{hubName || '—'}</span>
        ) : undefined
      }
      footer={footer}
    >
      <div className="px-5 py-5">
        {rider && (
          <>
            <MetricStrip rider={rider} />

            {!driverId ? (
              <div className="mt-5 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-3 text-[12px] text-text-sub-600">
                Unassigned invoices (no rider) — can't be filtered per rider.
                Assign a rider first to see the details.
              </div>
            ) : (
              <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold tracking-tight text-text-strong-950">
                    Bookings
                    <span className="ml-2 text-[11px] font-normal text-text-sub-600">
                      {total} booking{total === 1 ? '' : 's'}
                    </span>
                  </h4>
                </div>
                <SearchField
                  value={search}
                  onChange={setSearch}
                  placeholder="Search booking ID or invoice..."
                  size="sm"
                />
                {loading ? (
                  <div className="space-y-2">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className="h-16 animate-pulse rounded-lg bg-bg-soft-200"
                      />
                    ))}
                  </div>
                ) : groups.length === 0 ? (
                  <div className="rounded-lg border border-stroke-soft-200 px-3 py-6 text-center text-[12px] text-text-sub-600">
                    No bookings
                  </div>
                ) : (
                  <div className="space-y-2">
                    {groups.map((g) => {
                      const key = groupKey(g);
                      return (
                        <BookingGroupCard
                          key={key}
                          group={g}
                          collapsed={collapsed.has(key)}
                          onToggle={() => toggleGroup(key)}
                        />
                      );
                    })}
                    {/* Infinite-scroll sentinel + bottom loading state */}
                    <div ref={sentinelRef} aria-hidden className="h-px" />
                    {loadingMore ? (
                      <div className="flex items-center justify-center gap-2 py-3 text-[11px] text-text-sub-600">
                        <Spinner size="sm" />
                        Loading more…
                      </div>
                    ) : hasMore ? (
                      <div className="py-3 text-center text-[11px] text-text-sub-600">
                        Scroll to load more
                      </div>
                    ) : (
                      total > 0 && (
                        <div className="py-2 text-center text-[11px] text-text-sub-600">
                          All {total} booking{total === 1 ? '' : 's'} loaded
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </FormModal>
  );
};

const MetricStrip: React.FC<{ rider: RiderPerformanceRow }> = ({ rider }) => {
  const needsAction = rider.unresolvedByRider;
  const metrics: { label: string; value: string; alert?: boolean }[] = [
    { label: 'Held', value: String(rider.held) },
    {
      label: 'Oldest',
      value: rider.held >= 0 ? `${rider.oldestDaysSince ?? 0}d` : '—',
      alert: (rider.oldestDaysSince ?? 0) >= 7,
    },
    { label: 'Red ≥7d', value: String(rider.red), alert: rider.red > 0 },
    { label: 'Avg aging', value: `${rider.avgAging}d` },
  ];
  return (
    <div className="space-y-2">
      {/* Ops priority — invoices to chase the rider on (EXCEPTION + with rider). */}
      <div
        className={`rounded-lg border px-3 py-2.5 ${
          needsAction > 0
            ? 'border-warning-light bg-warning-lighter'
            : 'border-stroke-soft-200'
        }`}
      >
        <p className="text-[14px] font-bold uppercase text-text-strong-600">
          Needs follow-up
        </p>
        <div className="flex items-baseline gap-2">
          <p
            className={`text-[22px] font-bold tabular-nums ${
              needsAction > 0 ? 'text-warning-base' : 'text-text-strong-950'
            }`}
          >
            {needsAction}
          </p>
          {/* Breakdown doubles as the explanation and stops the total from
              looking like a duplicate of the Held tile below. */}
          <span className="text-[12px] text-text-sub-600 tabular-nums">
            {rider.held} with rider · {rider.exception} exception
          </span>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {metrics.map((m) => (
          <div
            key={m.label}
            className="rounded-lg border border-stroke-soft-200 px-3 py-2"
          >
            <p className="text-[10px] uppercase tracking-wide text-text-sub-600">
              {m.label}
            </p>
            <p
              className={`text-[18px] font-bold tabular-nums ${
                m.alert ? 'text-error-base' : 'text-text-strong-950'
              }`}
            >
              {m.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const BookingGroupCard: React.FC<{
  group: RiderBookingGroup;
  collapsed: boolean;
  onToggle: () => void;
}> = ({ group, collapsed, onToggle }) => {
  const hasBooking = group.bookingId !== null;
  const overdue = (group.oldestDaysSince ?? 0) >= 7;
  return (
    <div className="rounded-lg border border-stroke-soft-200">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <div className="flex min-w-0 items-center gap-2">
          <i
            className={`ri-arrow-right-s-line shrink-0 text-text-sub-600 transition-transform ${
              collapsed ? '' : 'rotate-90'
            }`}
          />
          <span
            className={`truncate ${
              hasBooking
                ? 'font-mono text-[12px] font-semibold text-text-strong-950'
                : 'text-[12px] italic text-text-sub-600'
            }`}
          >
            {group.bookingId ?? 'No booking ID'}
          </span>
          <span className="shrink-0 rounded-md bg-bg-weak-50 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-text-sub-600">
            {group.invoiceCount}
          </span>
        </div>
        {group.oldestDaysSince != null && (
          <span
            className={`shrink-0 text-[11px] tabular-nums ${
              overdue ? 'font-semibold text-error-base' : 'text-text-sub-600'
            }`}
          >
            {group.oldestDaysSince}d
          </span>
        )}
      </button>
      {!collapsed && (
        <div className="space-y-2 border-t border-stroke-soft-200 px-2 py-2">
          {group.invoices.map((inv) => (
            <InvoiceRow key={inv.id} invoice={inv} />
          ))}
        </div>
      )}
    </div>
  );
};

const InvoiceRow: React.FC<{ invoice: Invoice }> = ({ invoice }) => (
  <div className="rounded-lg border border-stroke-soft-200 px-3 py-2.5">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-mono text-[12px] font-semibold text-text-strong-950">
          {invoice.invoiceNumber}
        </p>
        <p className="truncate text-[11px] text-text-sub-600">
          {invoice.outlet?.name ?? '—'}
        </p>
      </div>
      <InvoiceStatusPill status={invoice.status} />
    </div>
    <div className="mt-2 flex items-center justify-between">
      <span className="text-[11px] text-text-sub-600 tabular-nums">
        Issued: {invoice.issueDate}
      </span>
      <AgingBadge tier={invoice.agingTier} daysSince={invoice.daysSince} />
    </div>
  </div>
);

export default RiderDetailPanel;
